import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { translations, LANGUAGES, RTL_LANGS } from "@/i18n/translations";
import { pages } from "@/i18n/pages";
import { extra } from "@/i18n/extra";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const I18nContext = createContext(null);

function detectDefault() {
  const saved = localStorage.getItem("mp_lang");
  if (saved && translations[saved]) return saved;
  // First-time visitor: match against their ordered browser language preferences.
  const prefs = (navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language || "en"]);
  for (const raw of prefs) {
    if (!raw) continue;
    const lower = raw.toLowerCase();
    if (translations[lower]) return lower;          // exact (e.g. "zh")
    const base = lower.split("-")[0];               // regional variant (e.g. "pt-BR" -> "pt")
    if (translations[base]) return base;
  }
  return "en";
}

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(detectDefault);

  useEffect(() => {
    localStorage.setItem("mp_lang", lang);
    document.documentElement.lang = lang;
    document.documentElement.dir = RTL_LANGS.includes(lang) ? "rtl" : "ltr";
  }, [lang]);

  const t = useCallback((key, fallback) => {
    const pick = (obj) => key.split(".").reduce((o, k) => (o ? o[k] : undefined), obj);
    return pick(translations[lang]) ?? pick(pages[lang]) ?? pick(extra[lang]) ?? pick(translations.en) ?? pick(pages.en) ?? pick(extra.en) ?? fallback ?? key;
  }, [lang]);

  const setLang = useCallback((code) => {
    if (!translations[code]) return;
    setLangState(code);
    api.put("/settings/preferences", { preferences: { language: code } }).catch(() => {});
  }, []);

  // Adopt server-stored preference once (without overwriting an explicit local choice on this device)
  const syncFromUser = useCallback((user) => {
    const pref = user?.preferences?.language;
    if (pref && translations[pref] && !localStorage.getItem("mp_lang")) setLangState(pref);
  }, []);

  return (
    <I18nContext.Provider value={{ lang, setLang, t, languages: LANGUAGES, syncFromUser }}>
      {children}
    </I18nContext.Provider>
  );
}

export const useI18n = () => useContext(I18nContext) || { lang: "en", setLang: () => {}, t: (k, f) => f || k, languages: LANGUAGES, syncFromUser: () => {} };

// Keeps the backend `preferences.language` aligned with the active UI language for
// logged-in users, so the AI companion & insights always respond in that language —
// even when the language was auto-detected from the browser (never manually picked).
export function LanguageSync() {
  const { lang } = useI18n();
  const { user, setUser } = useAuth();
  useEffect(() => {
    if (!user || !translations[lang]) return;
    if ((user.preferences?.language || "en") === lang) return;
    api.put("/settings/preferences", { preferences: { language: lang } })
      .then(() => setUser?.((u) => (u ? { ...u, preferences: { ...(u.preferences || {}), language: lang } } : u)))
      .catch(() => {});
  }, [user, lang, setUser]);
  return null;
}
